import React, { useState } from "react";
import { Form, Table } from "react-bootstrap";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faMessage, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useGetWorkflowMessagingHierarchyQuery } from "../store/slices/widgetsSlice.ts";
import MyPagination from "../Common/MyPagination.tsx";
import NoDataPlaceholder from "../Common/NoDataPlaceholder";
import RepliesModal from "./RealtimeReporting/RepliesModal";

const channelDetails = {
  sms: { icon: faMessage, label: "SMS" },
  email: { icon: faEnvelope, label: "Email" },
  voice: { icon: faPhone, label: "Voice" },
};

function transactionLink(item, channel, status, level) {
  const metadata = [`project:${item.project_uuid}`];
  if ((level === "process" || level === "workflow") && item.process_guid) metadata.push(`process:${item.process_guid}`);
  if (level === "workflow" && item.workflow_uuid) metadata.push(`workflow:${item.workflow_uuid}`);
  const params = new URLSearchParams({
    tab: "workflow",
    projectUUID: item.project_uuid,
    comms: channel,
    meta: metadata.join(","),
  });
  if (status) params.set("messageStatus", status);
  // Detailed reports stay over the Workflow tab so returning from a metric
  // never discards the selected hierarchy level.
  return `/reporting/realtime?${params.toString()}`;
}

function ChannelMetric({ item, metric, status, onShowReplies, level }) {
  const entries = Object.entries(item.channels || {}).filter(([channel, stats]) => channelDetails[channel] && (stats?.[metric] || 0) > 0);
  if (!entries.length) return "—";
  return entries.map(([channel, stats]) => {
    const count = stats?.[metric] || 0;
    const { icon, label } = channelDetails[channel];
    if (metric === "replies") {
      return <button className="btn btn-link p-0 me-2" key={channel} type="button" onClick={() => onShowReplies(channel)} title={`View ${count} ${label} replies}`}>
        <FontAwesomeIcon icon={icon} aria-hidden /> <span>{count}</span>
      </button>;
    }
    return <Link className="me-2" key={channel} to={transactionLink(item, channel, status, level)} title={`View ${count} ${label} ${metric}`}>
      <FontAwesomeIcon icon={icon} aria-hidden /> <span>{count}</span>
    </Link>;
  });
}

function when(value) {
  return value ? new Date(value).toLocaleString() : "-";
}

export default function WorkflowMessagingHierarchy() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(() => searchParams.get("workflowProject") ? { project_uuid: searchParams.get("workflowProject") } : null);
  const [process, setProcess] = useState(() => searchParams.get("workflowProcess") ? { process_guid: searchParams.get("workflowProcess") } : null);
  const [dates, setDates] = useState({ date_gte: "", date_lte: "" });
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [showReplies, setShowReplies] = useState(null);
  const [orderBy, setOrderBy] = useState(searchParams.get("workflowSort") || "latest_started_at");
  const [order, setOrder] = useState(searchParams.get("workflowOrder") || "desc");
  let level = "project";
  if (process) level = "workflow";
  else if (project) level = "process";
  const queryArgs = {
    level,
    project_uuid: project ? [project.project_uuid] : undefined,
    process_guid: process?.process_guid,
    ...dates,
    page,
    per_page: perPage,
    order_by: orderBy,
    order,
  };
  const { data = { components: [], total: 0 }, isFetching } =
    useGetWorkflowMessagingHierarchyQuery(queryArgs);

  const resetPage = (update) => {
    setPage(0);
    update();
  };
  const updateHierarchyURL = (next = {}) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", "workflow");
    Object.entries(next).forEach(([key, value]) => {
      if (value) params.set(key, value);
      else params.delete(key);
    });
    setSearchParams(params, { replace: true });
  };
  const selectProject = (item) => resetPage(() => {
    setProject(item);
    setProcess(null);
    updateHierarchyURL({ workflowProject: item.project_uuid, workflowProcess: "" });
  });
  const selectProcess = (item) => resetPage(() => {
    setProcess(item);
    updateHierarchyURL({ workflowProcess: item.process_guid });
  });
  const goBack = () => resetPage(() => {
    if (process) {
      setProcess(null);
      updateHierarchyURL({ workflowProcess: "" });
    } else {
      setProject(null);
      updateHierarchyURL({ workflowProject: "", workflowProcess: "" });
    }
  });
  const sortBy = (field) => {
    const nextOrder = orderBy === field && order === "desc" ? "asc" : "desc";
    setOrderBy(field);
    setOrder(nextOrder);
    setPage(0);
    updateHierarchyURL({ workflowSort: field, workflowOrder: nextOrder });
  };
  const sortLabel = (field, label) => `${label}${orderBy === field ? (order === "asc" ? " ↑" : " ↓") : ""}`;
  const titles = {
    project: "Project",
    workflow: "Workflow",
    process: "Process",
  };
  const title = titles[level];

  return (
    <div className="rounded-3 bg-white">
      <div className="panel-heading widget-header d-flex justify-content-between">
        <h3 className="panel-title padding-10">Workflow Messaging</h3>
      </div>
      <div className="panel-body p-3">
      <div className="asset-filter-panel mb-3 bg-colored-reverse p-3 rounded-3 text-white">
        <div className="d-flex flex-wrap align-items-end gap-3">
          <Form.Group>
            <Form.Label className="small mb-1">Sent from</Form.Label>
            <Form.Control
              aria-label="Sent from"
              type="datetime-local"
              value={dates.date_gte}
              onChange={(event) =>
                resetPage(() =>
                  setDates((value) => ({
                    ...value,
                    date_gte: event.target.value,
                  }))
                )
              }
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="small mb-1">Sent to</Form.Label>
            <Form.Control
              aria-label="Sent to"
              type="datetime-local"
              value={dates.date_lte}
              onChange={(event) =>
                resetPage(() =>
                  setDates((value) => ({
                    ...value,
                    date_lte: event.target.value,
                  }))
                )
              }
            />
          </Form.Group>
          {(project || process || dates.date_gte || dates.date_lte) && (
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() =>
                resetPage(() => {
                  setProject(null);
                  setProcess(null);
                  setDates({ date_gte: "", date_lte: "" });
                  updateHierarchyURL({ workflowProject: "", workflowProcess: "" });
                })
              }
            >
              Clear filters
            </button>
          )}
        </div>
      </div>
      {(project || process) && (
        <div className="mb-3 d-flex gap-2 align-items-center">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={goBack}
          >
            Back to {process ? "processes" : "projects"}
          </button>
          <span className="text-secondary">
            {project?.project_label}
            {process && ` / ${process.process_guid}`}
          </span>
        </div>
      )}
      <div className="table-responsive" key={`${level}:${project?.project_uuid || ""}:${process?.process_guid || ""}:${page}:${perPage}`}>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>{title}</th>
            <th><button type="button" className="btn btn-link p-0 text-reset" onClick={() => sortBy(level === "process" || level === "workflow" ? "process_started_at" : "latest_started_at")}>{sortLabel(level === "process" || level === "workflow" ? "process_started_at" : "latest_started_at", "Latest started")}</button></th>
            <th><button type="button" className="btn btn-link p-0 text-reset" onClick={() => sortBy("dispatched")}>{sortLabel("dispatched", "Sent")}</button></th>
            <th><button type="button" className="btn btn-link p-0 text-reset" onClick={() => sortBy("delivered")}>{sortLabel("delivered", "Delivered")}</button></th>
            <th><button type="button" className="btn btn-link p-0 text-reset" onClick={() => sortBy("failed")}>{sortLabel("failed", "Failed")}</button></th>
            <th><button type="button" className="btn btn-link p-0 text-reset" onClick={() => sortBy("replies")}>{sortLabel("replies", "Replies")}</button></th>
          </tr>
        </thead>
        <tbody>
          {data.components.map((item) => {
            const key =
                level === "project" ? item.project_uuid : level === "process" ? item.process_id : item.workflow_uuid;
            return (
              <tr key={key}>
                <td>
                  {level === "project" && (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => selectProject(item)}
                    >
                      {item.project_label}
                    </button>
                  )}
                  {level === "workflow" && (
                    <span>{item.workflow_label}</span>
                  )}
                  {level === "process" && (
                    <button type="button" className="btn btn-link p-0" onClick={() => selectProcess(item)}>{item.process_guid}</button>
                  )}
                </td>
                <td>
                  {when(item.process_started_at || item.latest_started_at)}
                </td>
                <td><ChannelMetric item={item} metric="dispatched" level={level} /></td>
                <td><ChannelMetric item={item} metric="delivered" status="delivered" level={level} /></td>
                <td><ChannelMetric item={item} metric="failed" status="failed" level={level} /></td>
                <td><ChannelMetric item={item} metric="replies" level={level} onShowReplies={(channel) => setShowReplies({ channel, context: level === "project" ? { kind: "project", value: item.project_uuid, channel } : { kind: "process", value: item.process_guid, channel } })} /></td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      </div>
      {!isFetching && !data.components.length && <NoDataPlaceholder />}
      <MyPagination
        total={data.total}
        onPaginate={(nextPage, nextPerPage) => {
          setPage(nextPage);
          setPerPage(nextPerPage);
        }}
        perPageOptions={[10, 25, 50]}
        defaultPerPage={10}
        alwaysVisible
      />
      {showReplies && <RepliesModal showReplies={showReplies} setShowReplies={setShowReplies} />}
      </div>
    </div>
  );
}
